// Drawing App - Canvas Rendering Engine

import { Layer, ViewTransform } from './types';
import { LayerManager } from './layer_manager';
import { TransformManager } from './transform';

export class CanvasEngine {
  private displayCanvas: HTMLCanvasElement;
  private displayCtx: CanvasRenderingContext2D;
  private layerManager: LayerManager;
  private transformManager: TransformManager;
  private animFrameId: number | null = null;
  private needsRender = true;
  private canvasWidth: number;
  private canvasHeight: number;
  private activeStroke: OffscreenCanvas | null = null;
  private activeStrokeOpacity: number = 1.0;
  private activeStrokeLayerId: string | null = null;
  private brushCursorPos: { x: number; y: number } | null = null;
  private brushCursorSize: number = 12;
  private brushCursorHardness: number = 0.8;
  private brushCursorVisible: boolean = false;
  private remoteOverlay: OffscreenCanvas | null = null;

  constructor(
    displayCanvas: HTMLCanvasElement,
    layerManager: LayerManager,
    transformManager: TransformManager,
    width: number = 2048,
    height: number = 2048
  ) {
    this.displayCanvas = displayCanvas;
    this.displayCtx = displayCanvas.getContext('2d')!;
    this.layerManager = layerManager;
    this.transformManager = transformManager;
    this.canvasWidth = width;
    this.canvasHeight = height;

    this.transformManager.onChange(() => this.requestRender());
    this.startRenderLoop();
  }

  getCanvasSize(): { width: number; height: number } {
    return { width: this.canvasWidth, height: this.canvasHeight };
  }

  requestRender(): void {
    this.needsRender = true;
  }

  private startRenderLoop(): void {
    const loop = () => {
      if (this.needsRender) {
        this.render();
        this.needsRender = false;
      }
      this.animFrameId = requestAnimationFrame(loop);
    };
    this.animFrameId = requestAnimationFrame(loop);
  }

  private render(): void {
    const ctx = this.displayCtx;
    const dw = this.displayCanvas.width;
    const dh = this.displayCanvas.height;
    const transform = this.transformManager.getTransform();

    // Clear display
    ctx.clearRect(0, 0, dw, dh);

    // Draw checkerboard background for transparency indication
    this.drawCheckerboard(ctx, transform);

    // Apply view transform
    ctx.save();
    ctx.translate(transform.offsetX, transform.offsetY);
    ctx.scale(transform.scale, transform.scale);

    // Draw canvas border
    ctx.strokeStyle = '#555';
    ctx.lineWidth = 1 / transform.scale;
    ctx.strokeRect(0, 0, this.canvasWidth, this.canvasHeight);

    // Composite layers bottom to top
    const layers = this.layerManager.getLayers();
    const activeLayerId = this.activeStrokeLayerId || this.layerManager.getActiveLayerId();
    for (const layer of layers) {
      if (!layer.visible || layer.opacity === 0) continue;

      ctx.globalAlpha = layer.opacity;
      ctx.globalCompositeOperation = layer.blendMode;
      ctx.drawImage(layer.canvas, 0, 0);

      // Draw active stroke on top of its layer
      if (this.activeStroke && layer.id === activeLayerId) {
        ctx.globalAlpha = this.activeStrokeOpacity;
        ctx.globalCompositeOperation = 'source-over';
        ctx.drawImage(this.activeStroke, 0, 0);
      }
    }

    // Draw remote overlay (collaborative strokes)
    if (this.remoteOverlay) {
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(this.remoteOverlay, 0, 0);
    }

    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.restore();

    // Draw brush cursor (in screen space, after restore)
    if (this.brushCursorVisible && this.brushCursorPos) {
      const screenPos = this.transformManager.canvasToScreen(this.brushCursorPos.x, this.brushCursorPos.y);
      const screenRadius = (this.brushCursorSize / 2) * transform.scale;
      this.drawBrushCursor(ctx, screenPos.x, screenPos.y, screenRadius, this.brushCursorHardness);
    }
  }

  private drawCheckerboard(ctx: CanvasRenderingContext2D, transform: ViewTransform): void {
    const size = 16;
    const startScreen = this.transformManager.canvasToScreen(0, 0);
    const endScreen = this.transformManager.canvasToScreen(this.canvasWidth, this.canvasHeight);

    // Clip to canvas bounds
    ctx.save();
    ctx.beginPath();
    ctx.rect(
      Math.max(0, startScreen.x),
      Math.max(0, startScreen.y),
      Math.min(this.displayCanvas.width, endScreen.x) - Math.max(0, startScreen.x),
      Math.min(this.displayCanvas.height, endScreen.y) - Math.max(0, startScreen.y)
    );
    ctx.clip();

    const scaledSize = size * transform.scale;
    const startX = Math.floor(Math.max(0, startScreen.x) / scaledSize) * scaledSize;
    const startY = Math.floor(Math.max(0, startScreen.y) / scaledSize) * scaledSize;

    for (let y = startY; y < Math.min(this.displayCanvas.height, endScreen.y); y += scaledSize) {
      for (let x = startX; x < Math.min(this.displayCanvas.width, endScreen.x); x += scaledSize) {
        const col = Math.floor((x - startScreen.x) / scaledSize);
        const row = Math.floor((y - startScreen.y) / scaledSize);
        ctx.fillStyle = (col + row) % 2 === 0 ? '#ffffff' : '#e0e0e0';
        ctx.fillRect(x, y, scaledSize, scaledSize);
      }
    }
    ctx.restore();
  }

  setActiveStroke(canvas: OffscreenCanvas | null, opacity: number = 1.0, layerId: string | null = null): void {
    this.activeStroke = canvas;
    this.activeStrokeOpacity = opacity;
    this.activeStrokeLayerId = layerId;
    this.requestRender();
  }

  setBrushCursor(x: number, y: number, size: number, hardness: number): void {
    this.brushCursorPos = { x, y };
    this.brushCursorSize = size;
    this.brushCursorHardness = hardness;
    this.brushCursorVisible = true;
    this.requestRender();
  }

  hideBrushCursor(): void {
    this.brushCursorVisible = false;
    this.requestRender();
  }

  setRemoteOverlay(canvas: OffscreenCanvas | null): void {
    this.remoteOverlay = canvas;
    this.requestRender();
  }

  private drawBrushCursor(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, hardness: number): void {
    if (radius < 1) return;

    ctx.save();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.stroke();

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.stroke();

    // Inner circle for hardness indication
    if (hardness < 0.9 && radius > 5) {
      const innerRadius = radius * hardness;
      ctx.setLineDash([]);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.arc(x, y, innerRadius, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Center dot
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.beginPath();
    ctx.arc(x, y, 1.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  resizeDisplay(width: number, height: number): void {
    this.displayCanvas.width = width;
    this.displayCanvas.height = height;
    this.requestRender();
  }

  getCompositeImage(): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = this.canvasWidth;
    canvas.height = this.canvasHeight;
    const ctx = canvas.getContext('2d')!;

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

    const layers = this.layerManager.getLayers();
    for (const layer of layers) {
      if (!layer.visible || layer.opacity === 0) continue;
      ctx.globalAlpha = layer.opacity;
      ctx.globalCompositeOperation = layer.blendMode;
      ctx.drawImage(layer.canvas, 0, 0);
    }

    return canvas;
  }

  destroy(): void {
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
    }
  }
}
